import React, { useEffect, useState } from 'react';
import { Box, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import NumberFormat from 'react-number-format';
import InputMask from 'react-input-mask';
import * as _ from 'lodash';

import { formatCurrency, formatNumberWithDelimiter } from '../../../../../../utils';

const styles = {
  textField: {
    margin: '16px 0 0 0',
    width: '100%',
    height: 40,

    '& input': {
      padding: 12,
      fontSize: 14,
    },
    '& fieldset:disabled': {
      borderColor: '#f44336',
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 24px',
    },
    '& fieldset legend span': {
      fontSize: 12,
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
      textTransform: 'none',
      fontWeight: 'normal',
    },
  },
};

const useStyles = makeStyles(styles);

const TextFieldComp = props => {
  const classes = useStyles();
  const {
    docuSignTabMap,
    handleBlur,
    inputProps,
    setReference,
    setReferenceToRequiredFunction,
    setReferenceToErrorsValueFunction,
    mask,
    index,
    type,
    validRegexPattern,
  } = props;
  const [value, setValue] = useState(docuSignTabMap.value ? docuSignTabMap.value : '');
  const [required, setRequired] = useState(docuSignTabMap.required);
  /*
  Errors have the shape:
  { name: string, message: string }[]
  */
  const [errors, setErrors] = useState(docuSignTabMap.errors);

  function hasRegexError(value) {
    if (validRegexPattern) {
      const validRegexPatternActual = new RegExp(validRegexPattern);
      if (
        _.isString(value) &&
        value
          .split('')
          .filter(c => validRegexPatternActual.test(c))
          .join('') !== value
      ) {
        return true;
      }
    }
    return false;
  }

  function doesHaveError(valueToCheck, field) {
    try {
      if (errors && errors.length > 0) {
        return true;
      }
    } catch (e) {
      console.log(`Error checking field.details.error in doesHaveError: ${JSON.stringify(field)}: ${e}`);
    }
    return (
      (!valueToCheck && valueToCheck !== 0 && required) || (_.isString(valueToCheck) && hasRegexError(valueToCheck))
    );
  }

  const [showInputErrorHelperText, setShowInputErrorHelperText] = useState(hasRegexError(docuSignTabMap.value));
  const [hasError, setHasError] = useState(doesHaveError(docuSignTabMap.value, docuSignTabMap));

  const setRequiredValue = newRequiredValue => {
    console.log(`Setting required of ${docuSignTabMap.tabField} to ${newRequiredValue}`);
    setRequired(newRequiredValue);
  };

  const setErrorsValue = errors => {
    setErrors(errors);
  };

  const formatValue = value => {
    let val = value;

    if (val === undefined || val === null || val === '') {
      val = '';
    }

    if (type === 'FLOAT' || type === 'MONEY') {
      val = parseFloat(val.toString().replace(/\$/g, '').replace(/,/g, '')).toFixed(2);
      if (!val || isNaN(val)) {
        val = parseFloat(0).toFixed(2);
      }
    }

    if (type === 'INTEGER' || type === 'FLOAT' || type === 'MONEY') {
      if (inputProps.max && val > inputProps.max) {
        val = inputProps.max;
      }
      if (inputProps.min && val < inputProps.min) {
        val = inputProps.min;
      }
    }

    if (type === 'INTEGER') {
      val = val.toString();
    }

    if (type === 'FEIN') {
      if (val && val.toString().replace(/\D/g, '').length === 8) {
        val = '0' + val;
      }
    }
    return val;
  };

  setReference(docuSignTabMap.label, v => {
    setValue(v);
    handleBlur(v, index);
  });
  setReferenceToRequiredFunction(docuSignTabMap.label, setRequiredValue);
  setReferenceToErrorsValueFunction(docuSignTabMap.label, setErrorsValue);

  useEffect(() => {
    if (docuSignTabMap.value !== value) {
      setValue('');
    }
  }, [props]);

  useEffect(() => {
    if (doesHaveError(value, docuSignTabMap)) {
      setHasError(true);
    } else {
      setHasError(false);
    }
  }, [value, required]);

  function doesMaskInputHaveAnError() {
    if (errors && errors.length > 0) {
      return true;
    }
    if (required && (docuSignTabMap.value === null || docuSignTabMap.value === undefined)) {
      return true;
    }
    if (!required && (docuSignTabMap.value === null || docuSignTabMap.value === undefined)) {
      return false;
    }
    if (type === 'PHONE_NUMBER') {
      const phoneNumberLength = docuSignTabMap.value.toString().replace(/[()\-_\s]/g, '').length;
      if (required) {
        return phoneNumberLength !== 10;
      } else {
        return phoneNumberLength !== 10 && phoneNumberLength !== 0;
      }
    }
    if (type === 'SSN') {
      const ssnFeinLength = docuSignTabMap.value.toString().replace(/[-_]/g, '').length;
      if (required) {
        return ssnFeinLength !== 9;
      } else {
        return ssnFeinLength !== 9 && ssnFeinLength !== 0;
      }
    }
    if (type === 'FEIN') {
      let mappedValue = docuSignTabMap.value.toString();
      if (mappedValue.replace(/\D/g, '').length === 8) {
        mappedValue = '0' + mappedValue;
      }
      const feinLength = mappedValue.replace(/\D/g, '').length;
      if (required) {
        return feinLength !== 9;
      } else {
        return feinLength !== 9 && feinLength !== 0;
      }
    }
    if (hasRegexError(docuSignTabMap.value)) {
      return true;
    }
  }

  let valueToDisplay = value;
  if (type === 'FLOAT') {
    valueToDisplay = formatNumberWithDelimiter(formatValue(value));
  } else if (type === 'MONEY') {
    valueToDisplay = formatCurrency(formatValue(value));
  } else if (type === 'FEIN') {
    valueToDisplay = formatValue(value);
  }

  return (
    <Box component="form" noValidate autoComplete="off" key={index} style={{ width: '100%' }}>
      {mask ? (
        <>
          <InputMask
            mask={mask}
            value={valueToDisplay}
            onBlur={() => {
              setValue(valueToDisplay);
              handleBlur(valueToDisplay, index);
            }}
            onChange={e => {
              let val = e.target.value;

              setValue(val);
            }}
          >
            {() => (
              <TextField
                key={index}
                className={classes.textField}
                fullWidth={true}
                label={(required ? '* ' : '') + docuSignTabMap.label}
                style={{ width: '100%' }}
                error={doesMaskInputHaveAnError()}
                variant="outlined"
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            )}
          </InputMask>
          {errors && errors.length > 0 && (
            <div style={{ color: 'red', fontSize: '0.75rem' }}>{errors.map(e => e.message).join(', ')}</div>
          )}
        </>
      ) : (
        <div>
          {type === 'FLOAT' || type === 'MONEY' ? (
            <NumberFormat
              value={value}
              thousandSeparator={true}
              prefix={type === 'MONEY' ? '$' : ''}
              decimalScale={type === 'MONEY' ? 2 : undefined}
              fixedDecimalScale={true}
              customInput={TextField}
              fullWidth={true}
              label={(required ? '* ' : '') + docuSignTabMap.label}
              variant="outlined"
              onBlur={() => {
                let formattedValue = formatValue(value, type);
                setValue(formattedValue);
                handleBlur(formattedValue, index);
              }}
              onValueChange={newValue => {
                let val = newValue.value;

                if (type === 'INTEGER') {
                  val = parseInt(val);
                  if (!val) {
                    val = 0;
                  }
                  if (inputProps.max && val > inputProps.max) {
                    val = inputProps.max;
                  }
                  if (inputProps.min && val < inputProps.min) {
                    val = inputProps.min;
                  }
                }

                if (type === 'FLOAT') {
                  val = parseFloat(val.replace(/,/g, ''));
                  if (!val) {
                    val = 0;
                  }
                  if (inputProps.max && val > inputProps.max) {
                    val = inputProps.max;
                  }
                  if (inputProps.min && val < inputProps.min) {
                    val = inputProps.min;
                  }
                }

                if (type === 'MONEY') {
                  val = parseFloat(val.replace(/[$]],/g, ''));
                  if (!val) {
                    val = 0;
                  }
                  if (inputProps.max && val > inputProps.max) {
                    val = inputProps.max;
                  }
                  if (inputProps.min && val < inputProps.min) {
                    val = inputProps.min;
                  }
                }

                if ((type !== 'STRING' && parseInt(val) < 0) || (type === 'YEAR' && parseInt(val) > 9999)) {
                  val = docuSignTabMap.value;
                }

                if (type === 'STRING') {
                  if (inputProps.maxLength && val.length > inputProps.maxLength) {
                    val = val.slice(0, inputProps.maxLength);
                  }
                }

                if (type === 'INITIALS') {
                  val = val.toUpperCase();
                }

                if (validRegexPattern) {
                  const validRegexPatternActual = new RegExp(validRegexPattern);
                  val = val
                    .split('')
                    .filter(char => validRegexPatternActual.test(char))
                    .join('');
                }

                setValue(val);
              }}
              className={classes.textField}
              error={hasError}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                ...inputProps,
              }}
            />
          ) : (
            <TextField
              key={index}
              className={classes.textField}
              fullWidth={true}
              label={(required ? '* ' : '') + docuSignTabMap.label}
              value={valueToDisplay}
              onBlur={() => {
                let formattedValue = formatValue(value, type);
                setValue(formattedValue);
                handleBlur(formattedValue, index);
              }}
              style={{ width: '100%' }}
              onChange={e => {
                let val = e.target.value;

                if (type === 'INTEGER') {
                  val = parseInt(val);
                  if (!val) {
                    val = 0;
                  }
                  if (inputProps.max && val > inputProps.max) {
                    val = inputProps.max;
                  }
                  if (inputProps.min && val < inputProps.min) {
                    val = inputProps.min;
                  }
                }

                if (type === 'FLOAT') {
                  val = parseFloat(val.replace(/,/g, ''));
                  if (!val) {
                    val = 0;
                  }
                  if (inputProps.max && val > inputProps.max) {
                    val = inputProps.max;
                  }
                  if (inputProps.min && val < inputProps.min) {
                    val = inputProps.min;
                  }
                }

                if ((type !== 'STRING' && parseInt(val) < 0) || (type === 'YEAR' && parseInt(val) > 9999)) {
                  val = docuSignTabMap.value;
                }

                if (type === 'STRING') {
                  if (inputProps.maxLength && val.length > inputProps.maxLength) {
                    val = val.slice(0, inputProps.maxLength);
                  }
                }

                if (type === 'INITIALS') {
                  val = val.toUpperCase();
                }

                if (validRegexPattern) {
                  const validRegexPatternActual = new RegExp(validRegexPattern);
                  val = val
                    .split('')
                    .filter(char => validRegexPatternActual.test(char))
                    .join('');
                }

                setValue(val);
              }}
              variant="outlined"
              error={hasError}
              slotProps={{
                htmlInput:
                  (type !== 'STRING' && {
                    type: type !== 'STRING' ? 'number' : 'string',
                    step: type === 'FLOAT' ? '0.01' : '1',
                    min: '1',
                    max: type === 'YEAR' ? '9999' : undefined,
                  },
                  { ...inputProps }),
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          )}
          <div>
            {/* {showInputErrorHelperText && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>{field.details?.inputErrorHelperText}</div>
            )} */}
            {errors && errors.length > 0 && (
              <div style={{ color: 'red', fontSize: '0.75rem' }}>{errors.map(e => e.message).join(', ')}</div>
            )}
          </div>
        </div>
      )}
    </Box>
  );
};

export default TextFieldComp;
